import { Banner, Button, LayerCard, LinkButton, Text } from '@cloudflare/kumo'
import { CheckCircleIcon, CopyIcon, ExportIcon } from '@phosphor-icons/react'
import type { GuestInvitationCreateResult } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'
import { formatWeekdayDate } from '../../lib/format'
import { useShareLink } from './useShareLink'
import './guests.css'

export interface InvitationReadyProps {
  invitation: GuestInvitationCreateResult
  /** Room name when the invitation is linked to a booking. */
  room: string | null
  onInviteAnother: () => void
}

/** Success state after creating an invitation: summary, share/copy, next steps. */
export function InvitationReady({ invitation, room, onInviteAnother }: InvitationReadyProps) {
  const { t, locale } = useTranslation()
  const share = useShareLink()
  // The window the API actually granted (a clamped begin shows up here).
  const when = `${formatWeekdayDate(invitation.visitDate, locale ?? 'pt-BR')} · ${invitation.visitTimeBegin} – ${invitation.visitTimeEnd}`

  return (
    <div className="guest-ready" role="status">
      <div className="flex items-center gap-2">
        <CheckCircleIcon
          size={28}
          weight="fill"
          className="text-kumo-success shrink-0"
          aria-hidden
        />
        <Text as="h2" variant="heading2">
          {t('guestInvitations.success.ready', { name: invitation.guestName })}
        </Text>
      </div>

      <LayerCard>
        <LayerCard.Primary>
          <dl className="guest-ready__facts">
            <dt>{t('guestInvitations.success.when')}</dt>
            <dd>{when}</dd>
            {invitation.roomBookingId && (
              <>
                <dt>{t('guestInvitations.success.booking')}</dt>
                <dd>{room ?? t('dashboard.nextUp.room')}</dd>
              </>
            )}
            <dt>{t('guestInvitations.success.entries')}</dt>
            <dd>
              {invitation.singleAccess
                ? t('guestInvitations.success.entriesSingle')
                : t('guestInvitations.success.entriesMany')}
            </dd>
          </dl>
        </LayerCard.Primary>
      </LayerCard>

      {invitation.guestEmailSent === true && (
        <Text as="p" variant="secondary">
          {t('guestInvitations.success.emailSent', { email: invitation.guestEmail ?? '' })}
        </Text>
      )}
      {invitation.guestEmailSent === false && (
        <Banner variant="alert" data-testid="guest-invitation-email-failed">
          {t('dashboard.inviteSheet.emailFailed', { email: invitation.guestEmail ?? '' })}
        </Banner>
      )}
      {invitation.guestEmailSent === null && (
        <Text as="p" variant="secondary">
          {t('guestInvitations.success.shareYourself')}
        </Text>
      )}

      <div className="guest-ready__actions">
        <Button
          variant="primary"
          className="guest-btn"
          icon={<ExportIcon size={18} />}
          onClick={() => void share(invitation.invitationUrl)}
        >
          {t('dashboard.inviteSheet.shareLink')}
        </Button>
        <Button
          variant="secondary"
          className="guest-btn"
          icon={<CopyIcon size={18} />}
          onClick={() => void share(invitation.invitationUrl, true)}
        >
          {t('dashboard.inviteSheet.copyLink')}
        </Button>
      </div>
      <div className="guest-ready__actions">
        <Button variant="ghost" className="guest-btn" onClick={onInviteAnother}>
          {t('guestInvitations.success.inviteAnother')}
        </Button>
        <LinkButton href="/my/guests" variant="ghost" className="guest-btn">
          {t('guestInvitations.success.done')}
        </LinkButton>
      </div>
    </div>
  )
}
